import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { hashHistory } from 'react-router';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _has from 'lodash/has';
import queryString from 'query-string';
// Models
import mProduct from 'models/product';
import { getTokenIsUser, mutLoginByMagicLink } from 'models/auth';
// HOCs
import withPermissions from 'hocs/WithPermissions';
import withMessageDialog from 'hocs/WithMessageDialog';
import {
  reinitAuth,
  reinitState,
  login,
  logout,
  postLogin,
  postLoginByMagicLink,
  getMe,
  postRecoverPassword,
} from 'actions';
import { authShape, meDataShape, errorShape } from 'constants/PropTypesShapes';
import LoadingDisplayer from 'components/misc/LoadingDisplayer';
import LoginUtils from 'utils/LoginUtils';
import { LOGIN_FORM_TYPES, LOGIN_FORM_TYPE_PARAMETER } from 'constants/Login';

const PRODUCT = __PRODUCT__; // eslint-disable-line no-undef
const isCpmClient = mProduct.isCpmClient(__PRODUCT__); // eslint-disable-line no-undef
// Specific require
/* eslint-disable import/no-dynamic-require */
const FormContainer = require(`./${PRODUCT}/FormContainer`);

const Login = ({
  auth,
  me,
  hasPermission,
  slugId,
  location: { query, pathname },
  dispatchGetMe,
  dispatchSignIn,
  dispatchSignInByMagicLink,
  dispatchPostRecoverPassword,
  dispatchReinitState,
  dispatchLogout,
  dispatchPostLogin,
}) => {
  const DEFAULT_FORM_TYPE = isCpmClient ? LOGIN_FORM_TYPES.SIGNUP : LOGIN_FORM_TYPES.SIGNIN;

  const initialFormType = _has(query, 'next') ? LOGIN_FORM_TYPES.SIGNIN : _get(query, LOGIN_FORM_TYPE_PARAMETER, DEFAULT_FORM_TYPE);

  const [loading, setLoading] = React.useState(true);
  const [currentFormType, setCurrentFormType] = React.useState(initialFormType);

  /* ========================================================= */
  /* PROCESS                                                   */
  /* ========================================================= */

  /* Authentication at initialization                          */
  /* ========================================================= */

  // Try to log in with magic link of token owned by user when component is initialized
  React.useEffect(function tryToLogInWithAuthToken() {
    setLoading(false);

    // Reinit auth to reset errors and "me" data
    // dispatchReinitAuth();

    if (auth) {
      const { token } = auth;
      if (getTokenIsUser(token)) {
        setLoading(true);
        dispatchGetMe();
      }
    }
  }, []);

  /* Loaders                                                   */
  /* ========================================================= */
  React.useEffect(function stopLoadingIfError() {
    if (auth.error && !auth.loading) {
      setLoading(false);
    }
  }, [auth.error]);

  /* Me                                                        */
  /* ========================================================= */
  // Load me data
  React.useEffect(function loadMeAfterAuthentication() {
    if (auth.loggedIn && !me.loading) {
      dispatchGetMe();
    }
  }, [auth.loggedIn]);
  /* ✅ ⬇️ */
  // Sign in and redirect once me is loaded
  React.useEffect(function signInAndRedirectAfterGettingMe() {
    if (!me.loading && !me.logout && me.data) {
      dispatchSignIn();

      const PARAM_FOR_REDIRECTION = 'next';
      const customRedirection = query && query[PARAM_FOR_REDIRECTION];

      let pathToRedirect = '/';

      // Case 1️⃣ | Default redirect
      if (!customRedirection) {
        pathToRedirect = LoginUtils.parseAvailablePathsToFindTheFirstAllowed({
          availablePaths: mProduct.getPaths(),
          context: mProduct.getContext(),
          hasPermission,
        });
      }

      // Case 2️⃣ | Redirect to custom URL
      if (customRedirection) {
        pathToRedirect = LoginUtils.generateNextPathFromUrl({
          query,
          parameterThatGetsPathForRedirection: PARAM_FOR_REDIRECTION,
          parametersToRemove: [PARAM_FOR_REDIRECTION, LOGIN_FORM_TYPE_PARAMETER],
        });
      }

      // Add leading "/" if missing
      const stringAndMissingLeadingSlash = typeof pathToRedirect === 'string' && pathToRedirect.charAt(0) !== '/' && pathToRedirect.length > 0;
      if (stringAndMissingLeadingSlash) pathToRedirect = `/${pathToRedirect}`;

      const objectAndMissingLeadingSlash = typeof pathToRedirect === 'object' && pathToRedirect !== null && pathToRedirect.pathname && pathToRedirect.pathname.charAt(0) !== '/';
      if (objectAndMissingLeadingSlash) pathToRedirect.pathname = `/${pathToRedirect.pathname}`;

      hashHistory.push(pathToRedirect);
    }
  }, [me]);

  /* Magic Link                                                */
  /* ========================================================= */
  const magicLink = _get(query, 'magic_link', null);
  React.useEffect(() => {
    if (magicLink) dispatchSignInByMagicLink({ link: magicLink });
  }, []);
  /* ✅ ⬇️ */
  React.useEffect(() => {
    if (auth.loggedByMagicLink && auth.token) {
      const PARAM_FOR_REDIRECTION = 'next';
      const pathToRedirect = LoginUtils.generateNextPathFromUrl({
        query,
        parameterThatGetsPathForRedirection: PARAM_FOR_REDIRECTION,
        parametersToRemove: [PARAM_FOR_REDIRECTION, LOGIN_FORM_TYPE_PARAMETER, 'magic_link'],
      });
      if (pathToRedirect) {
        // I put temporary an artificial delay for redirection because with the new authentification process
        // it seems the redirection and the fetch for on demand or booking on the redirected page is made without auth token so an error is sent back
        // Why ? Because event store has been updated with token booking or on demand fetch request
        // is triggered without this information. To be fixed with Flow 3...
        setTimeout(() => hashHistory.push(pathToRedirect), 500);
      }
    }
  }, [auth.loggedByMagicLink]);

  /* Error */
  /* ========================================================= */
  React.useEffect(function removeLoaderAndLogOutIfMeError() {
    if (me.error) {
      dispatchLogout();
      setLoading(false);
    }
  }, [me.error]);

  /* Ensure local state in URL always reflects chosen form type */
  /* ========================================================= */

  const handleFormTypeChange = (newFormType) => {
    setCurrentFormType(newFormType);

    if (newFormType === LOGIN_FORM_TYPES.FORGOT_PASSWORD) {
      dispatchReinitState('auth');
    }
  };

  React.useEffect(function updateUrlParamWhenFormTypeToDisplayChange() {
    if (isCpmClient && (!query[LOGIN_FORM_TYPE_PARAMETER] || query[LOGIN_FORM_TYPE_PARAMETER] !== currentFormType)) {
      const queryWithUpdatedParam = { ...query, [LOGIN_FORM_TYPE_PARAMETER]: currentFormType };
      hashHistory.replace({
        pathname,
        search: `?${queryString.stringify(queryWithUpdatedParam, { encode: true })}`,
      });
    }
  }, [currentFormType]);

  React.useEffect(function updateFormTypeWhenUrlChanges() {
    if (isCpmClient && query[LOGIN_FORM_TYPE_PARAMETER] && query[LOGIN_FORM_TYPE_PARAMETER] !== currentFormType) {
      const forceDisplaySignIn = query.next;
      if (forceDisplaySignIn) {
        handleFormTypeChange(LOGIN_FORM_TYPES.SIGNIN);
      } else {
        handleFormTypeChange(query[LOGIN_FORM_TYPE_PARAMETER]);
      }
    }
  }, [query[LOGIN_FORM_TYPE_PARAMETER]]);

  React.useEffect(function removeFormTypeFromParamsOnceLoggedIn() {
    const userIsLoggedIn = auth.loggedIn;
    const thereIsFormTypeParam = query[LOGIN_FORM_TYPE_PARAMETER];
    if (userIsLoggedIn && thereIsFormTypeParam) {
      const { [LOGIN_FORM_TYPE_PARAMETER]: formTypeParamValue, ...queryWithoutFormType } = query;
      hashHistory.replace({
        pathname,
        search: `?${queryString.stringify(queryWithoutFormType, { encode: true })}`,
      });
    }
  }, [auth.loggedIn]);

  /* ========================================================= */
  /* HANDLERS                                                  */
  /* ========================================================= */

  const handleSignIn = ({
    email, password,
  }) => {
    setLoading(true);
    dispatchPostLogin({ email, password, slugId });
  };

  const handleForceHideLoading = () => {
    setLoading(false);
  };

  const handleRecoverPassword = (data) => {
    setLoading(true);
    dispatchPostRecoverPassword(data);
  };
  /* ✅ ⬇️ */
  React.useEffect(function stopSpinnerAfterSendingPasswordLink() {
    if (auth && auth.recoverPassword && auth.recoverPassword.post && loading) {
      setLoading(false);
    }
  }, [auth]);

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */
  return (
    <LoadingDisplayer
      loading={loading}
      mountChildrenWhileLoading
      forceHideChildren={Boolean(magicLink)}
      withShrink
      onForceHide={handleForceHideLoading}
    >
      <FormContainer
        auth={auth}
        formType={currentFormType}
        handleFormTypeChange={handleFormTypeChange}
        handleRecoverPassword={handleRecoverPassword}
        handleSignIn={handleSignIn}
        query={query}
        pathname={pathname}
      />
    </LoadingDisplayer>
  );
};

/* ========================================================= */
/* PROP TYPES                                                */
/* ========================================================= */

Login.propTypes = {
  location: PropTypes.shape({
    query: PropTypes.shape({
      magic_link: PropTypes.string,
      next: PropTypes.string,
      validateAccountLink: PropTypes.string,
      form_type: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
    }).isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  auth: authShape.isRequired,
  me: PropTypes.shape({
    error: errorShape,
    loading: PropTypes.bool.isRequired,
    logout: PropTypes.bool.isRequired,
    data: meDataShape,
  }).isRequired,
  slugId: PropTypes.number,
  intlLocale: PropTypes.string.isRequired,
  hasPermission: PropTypes.func.isRequired,
  dispatchReinitAuth: PropTypes.func.isRequired,
  dispatchSignIn: PropTypes.func.isRequired,
  dispatchPostRecoverPassword: PropTypes.func.isRequired,
  dispatchSignInByMagicLink: PropTypes.func.isRequired,
  dispatchLogout: PropTypes.func.isRequired,
  dispatchPostLogin: PropTypes.func.isRequired,
  dispatchGetMe: PropTypes.func.isRequired,
  dispatchReinitState: PropTypes.func.isRequired,

};

Login.defaultProps = {
  slugId: null,
};

/* ========================================================= */
/* STORE                                                     */
/* ========================================================= */

const mapStateToProps = (state) => ({
  auth: state.auth,
  me: state.me,
  intlLocale: state.intl.locale,
  slugId: _get(state.cpmSlug, 'current.result.id'),
  checkCustomerAccountLink: _get(state, 'cpmAccount.checkCustomerAccountLink.result', null),
  validateCustomerAccount: _get(state, 'cpmAccount.validateCustomerAccount.result', null),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps;

  return {
    ...ownProps,
    ...stateProps,
    dispatchReinitAuth: () => {
      dispatch(reinitAuth());
    },
    dispatchReinitState: (target) => {
      dispatch(reinitState(target));
    },
    dispatchSignIn: () => {
      dispatch(login());
    },
    dispatchSignInByMagicLink: (data) => {
      dispatch(postLoginByMagicLink(mutLoginByMagicLink, { ...data, slugId: stateProps.slugId }));
    },
    dispatchLogout: () => {
      dispatch(logout());
    },
    dispatchPostLogin: (data) => {
      dispatch(postLogin(data));
    },
    dispatchPostRecoverPassword: (data) => {
      dispatch(postRecoverPassword(data));
    },
    dispatchGetMe: () => {
      dispatch(getMe());
    },
  };
};

/* ========================================================= */
/* EXPORT                                                   */
/* ========================================================= */

module.exports = compose(connect(mapStateToProps, null, mergeProps), withPermissions, withMessageDialog)(Login);



// WEBPACK FOOTER //
// ./routes/login/components/Login.js